import { getMeal } from "@/packages/dashboard/api/get-meal";
import { getMeals } from "@/packages/dashboard/api/get-meals";
import type { Meal, MealItem } from "@/packages/dashboard/api/get-meals";
import { Alert, Card, Center, Loader, SimpleGrid, Stack, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import type { FormEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { FoodItem } from "./food-item-combobox";

import { MealBoardPanel } from "./meal-board-panel";
import { MealFormPanel } from "./meal-form-panel";
import { MealTimestampSection } from "./meal-timestamp-section";
import { useMealFocus } from "./use-meal-focus";
import { FocusTarget, MealEntry, useMealForm } from "./use-meal-form";
import { useNewFoodFlow } from "./use-new-food-flow";
import { aggregateMeals } from "@/packages/dashboard/Home/Meals/macro-aggregations";
import { MealDetailsAccordion } from "@/packages/dashboard/Home/Meals/MealsSummarySections";
import { buildDayTimestampParams, normalizeDate } from "@/packages/dashboard/utils/date-range-helpers";

export const LogMeals = () => {
  // Read the meal id from the router so we know when to enter edit mode.
  const { mealId: mealIdParam } = useParams<{ mealId?: string }>();
  const mealIdNumber = mealIdParam !== undefined ? Number(mealIdParam) : null;
  const isMealIdInvalid = mealIdParam !== undefined && (mealIdNumber === null || Number.isNaN(mealIdNumber));
  const resolvedMealId = isMealIdInvalid ? null : mealIdNumber;
  const isEditingExistingMeal = resolvedMealId !== null;

  // Track timestamp state and focus requests that shuttle between the form and helper hooks.
  const [mealTimestamp, setMealTimestamp] = useState<Date | null>(() => (isEditingExistingMeal ? null : new Date()));
  const [queuedFocus, setQueuedFocus] = useState<FocusTarget | null>(null);
  const [hydratedMealId, setHydratedMealId] = useState<number | null>(null);

  // Fetch the selected meal (if any) so the form can hydrate with existing entries.
  const {
    data: mealResponse,
    isLoading: isMealLoading,
    isError: isMealError,
    error: mealError,
  } = useQuery({
    queryKey: ["meal", resolvedMealId],
    queryFn: () => {
      if (resolvedMealId === null) {
        throw new Error("Missing meal identifier");
      }

      return getMeal(resolvedMealId);
    },
    enabled: isEditingExistingMeal,
    staleTime: 60_000,
  });

  const mealData: Meal | undefined = mealResponse?.meal;
  // Convert API items into the structure useMealForm expects, memoized per response.
  const serverEntries = useMemo(() => (mealData ? mapMealItemsToFormEntries(mealData.meal_items) : []), [mealData]);

  // Fetch all meals for the current day to show in the journal
  const dayQueryParams = useMemo(() => buildDayTimestampParams(mealTimestamp), [mealTimestamp]);

  const {
    data: dayMealsResponse,
  } = useQuery({
    queryKey: ["meals", dayQueryParams?.start_timestamp, dayQueryParams?.end_timestamp],
    queryFn: () => {
      if (!dayQueryParams) {
        throw new Error("Missing date range for meals request");
      }

      return getMeals(dayQueryParams);
    },
    enabled: Boolean(dayQueryParams),
  });

  const dayMeals = dayMealsResponse?.meals ?? [];
  const dayAggregates = useMemo(() => aggregateMeals(dayMeals), [dayMeals]);
  const { dayGroups } = dayAggregates;

  // Core meal form hook keeps all entry state and submission logic in one place.
  const mealForm = useMealForm({ mealTimestamp });
  const { replaceMealEntries } = mealForm;

  // Manage the nested "create new food" flow that lives under this route.
  const newFoodFlow = useNewFoodFlow({
    onStartDraft: () => {
      mealForm.prepareForNewFoodDraft();
    },
    onFoodCreated: (item) => {
      const focusTarget = mealForm.applyNewFoodItem(item);
      setQueuedFocus(focusTarget);
    },
    onCancel: () => {
      setQueuedFocus("food");
    },
  });

  const { outlet, isActive: isNestedRouteActive, startCreateFlow } = newFoodFlow;

  // Coordinate focus between fields while respecting when the nested outlet is active.
  const { foodInputRef, quantityInputRef, focusNow, queueFocus } = useMealFocus({
    isBlocked: isNestedRouteActive,
  });

  // Apply queued focus changes once the UI is ready (or stash them until it is).
  useEffect(() => {
    if (!queuedFocus) {
      return;
    }

    if (isNestedRouteActive) {
      queueFocus(queuedFocus);
    } else {
      focusNow(queuedFocus);
    }

    setQueuedFocus(null);
  }, [focusNow, isNestedRouteActive, queueFocus, queuedFocus]);

  // Hydrate the form whenever we transition between create/edit modes or load a new meal.
  useEffect(() => {
    if (!isEditingExistingMeal) {
      setMealTimestamp(new Date());
      setHydratedMealId(null);
      replaceMealEntries([]);
      return;
    }

    if (!mealData) {
      return;
    }

    if (hydratedMealId === mealData.id) {
      return;
    }

    const timestamp = normalizeDate(mealData.timestamp);
    setMealTimestamp(timestamp ?? new Date());
    replaceMealEntries(serverEntries);
    setHydratedMealId(mealData.id);
    setQueuedFocus(null);
  }, [hydratedMealId, isEditingExistingMeal, mealData, replaceMealEntries, serverEntries]);

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    const focusTarget = mealForm.submit(event);
    if (focusTarget) {
      setQueuedFocus(focusTarget);
    }
  };

  const handleFoodItemChange = (item: FoodItem | null) => {
    const focusTarget = mealForm.changeFoodItem(item);
    if (focusTarget) {
      setQueuedFocus(focusTarget);
    }
  };

  const handleQuantityChange = mealForm.changeQuantity;

  const handleEntryDelete = (entry: MealEntry) => {
    mealForm.deleteEntry(entry);
  };

  const addFoodContent = outlet ?? (
    <MealFormPanel
      formState={mealForm.formState}
      isSubmitDisabled={mealForm.isSubmitDisabled}
      foodInputRef={foodInputRef}
      quantityInputRef={quantityInputRef}
      onSubmit={handleFormSubmit}
      onFoodItemChange={handleFoodItemChange}
      onQuantityChange={handleQuantityChange}
      onRequestCreateFoodItem={startCreateFlow}
    />
  );

  const showInvalidIdState = isMealIdInvalid;
  const showLoadingState = isEditingExistingMeal && isMealLoading;
  const showErrorState = isEditingExistingMeal && isMealError;
  const content = showInvalidIdState ? (
    <Alert color="red" title="Invalid meal identifier">
      The meal identifier provided in the URL is not valid.
    </Alert>
  ) : showLoadingState ? (
    <Center h={320}>
      <Loader />
    </Center>
  ) : showErrorState ? (
    <Alert color="red" title="Unable to load meal">
      {mealError instanceof Error ? mealError.message : "An unexpected error occurred while loading the meal."}
    </Alert>
  ) : (
    <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="xl" style={{ alignItems: "flex-start" }}>
      {addFoodContent}
      <MealBoardPanel
        mealItems={mealForm.mealItems}
        mealTimestamp={mealTimestamp}
        currentMealId={resolvedMealId}
        onDeleteEntry={handleEntryDelete}
      />
    </SimpleGrid>
  );

  return (
    <div className={"max-w-4xl"}>
      <Stack gap="lg">
        <Title order={2}>{isEditingExistingMeal ? "Edit Meal" : "Log Meal"}</Title>

        <Card shadow="sm" padding="lg" withBorder>
          <Stack gap="lg">
            <MealTimestampSection
              value={mealTimestamp}
              onChange={setMealTimestamp}
            />

            {content}

            <MealDetailsAccordion dayGroups={dayGroups} />
          </Stack>
        </Card>
      </Stack>
    </div>
  );
};

// Align the API meal items with the shape useMealForm expects for its internal state.
const mapMealItemsToFormEntries = (items: MealItem[]): MealEntry[] =>
  items.map((item) => {
    const quantity = Number(item.quantity);
    const safeQuantity = Number.isFinite(quantity) ? quantity : 0;
    const foodItem = toFoodItem(item);

    return {
      clientId: `persisted-${item.id}`,
      id: item.id,
      foodItem,
      quantity: safeQuantity,
    };
  });

// Build a FoodItem usable by the form, falling back to sensible defaults if the API omits details.
const toFoodItem = (item: MealItem): FoodItem => {
  const fallbackName = "Unknown food";
  const food = item.food_item;

  if (!food) {
    return {
      id: item.food_item_id,
      name: fallbackName,
      description: null,
      serving_description: null,
      serving_unit: "",
      serving_size: 1,
      calories: 0,
      proteins: 0,
      carbs: 0,
      fats: 0,
    };
  }

  const parseNumeric = (value: string | undefined | null): number => {
    if (!value) {
      return 0;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  return {
    id: food.id,
    name: food.name,
    description: food.description ?? null,
    serving_description: food.serving_description ?? null,
    serving_unit: food.serving_unit,
    serving_size: food.serving_size,
    calories: food.calories,
    proteins: food.proteins,
    carbs: food.carbs,
    fats: food.fats,
  };
};
